import { Angle, Vector2, Vector3 } from "@babylonjs/core";
import * as pclipping from 'polygon-clipping';
import { FlatPolygon } from "../models/flat-polygon";
import { Point2, Point3 } from "../models/point.type";
import { Polygon } from "../models/polygon";

export class UtilityService {
    knapsack(items: { w: number, b: number }[], capacity: number) {
        let indexItem = 0,
            indexWeight = 0,
            prevMaximum = 0,
            currentMaximum = 0,
            numberItems = items.length,
            weighs = new Array(numberItems + 1),
            keeps = new Array(numberItems + 1),
            solutionSet = [];

        for (indexItem = 0; indexItem < numberItems + 1; indexItem++) {
            weighs[indexItem] = new Array(capacity + 1);
            keeps[indexItem] = new Array(capacity + 1);
        }

        for (indexItem = 0; indexItem <= numberItems; indexItem++) {
            for (indexWeight = 0; indexWeight <= capacity; indexWeight++) {

                if (indexItem === 0 || indexWeight === 0) {
                    weighs[indexItem][indexWeight] = 0;
                }
                else if (items[indexItem - 1].w <= indexWeight) {
                    currentMaximum = items[indexItem - 1].b + weighs[indexItem - 1][indexWeight - items[indexItem - 1].w];
                    prevMaximum = weighs[indexItem - 1][indexWeight];
                    if (currentMaximum > prevMaximum) {
                        weighs[indexItem][indexWeight] = currentMaximum;
                        keeps[indexItem][indexWeight] = 1;
                    }
                    else {
                        weighs[indexItem][indexWeight] = prevMaximum;
                        keeps[indexItem][indexWeight] = 0;
                    }
                }

                else {
                    weighs[indexItem][indexWeight] = weighs[indexItem - 1][indexWeight];
                }
            }
        }

        indexWeight = capacity;
        indexItem = numberItems;
        for (indexItem; indexItem > 0; indexItem--) {
            if (keeps[indexItem][indexWeight] === 1) {
                solutionSet.push(items[indexItem - 1]);
                indexWeight = indexWeight - items[indexItem - 1].w;
            }
        }
        return { max: weighs[numberItems][capacity], solution: solutionSet };
    }

    calculatePolygonArea(points: Point3[]) {
        let area = 0;
        if (points && points.length > 0) {
            let vertices = points.map(s => ({ x: s[0], y: s[2] }));
            var total = 0;

            for (var i = 0, l = vertices.length; i < l; i++) {
                var addX = vertices[i].x;
                var addY = vertices[i === vertices.length - 1 ? 0 : i + 1].y;
                var subX = vertices[i === vertices.length - 1 ? 0 : i + 1].x;
                var subY = vertices[i].y;

                total += (addX * addY * 0.5);
                total -= (subX * subY * 0.5);
            }
            area = Math.abs(total);
        }
        return area;
    }

    keepInBounds(val: number, min: number, max: number): number {
        let result = 0;
        if (val < max && val > min) {
            result = val;
        } else if (val >= max) {
            result = max;
        } else if (val <= min) {
            result = min;
        }
        return result;
    }

    recurseNameSearch(fileName: string, names: string[], increment: number): string {
        const exists = names.find(name => name === fileName);
        const separator = ' (';
        if (exists) {
            let fileNameToRecurse;
            if (fileName.includes(separator)) {
                fileNameToRecurse = fileName.split(separator)[0];
            } else {
                fileNameToRecurse = fileName;
            }
            return this.recurseNameSearch(fileNameToRecurse + separator + increment++ + ')', names, increment++);
        } else {
            return fileName;
        }
    }

    download(filename: string, text: string) {
        var element = document.createElement('a');
        element.setAttribute('href', 'data:application/json;charset=utf-8,' + encodeURIComponent(text));
        element.setAttribute('download', filename);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    // based on "Distance per pixel math"
    // https://wiki.openstreetmap.org/wiki/Zoom_levels
    metersPerPixelMath(zoom: number, lat: number): number {
        const C = 40075016.686;
        const radiansLat = lat * Math.PI / 180.;
        const Stile = C * Math.cos(radiansLat) / Math.pow(2, zoom);
        const Spixel = Stile / 256;
        return Spixel;
    }

    removeShortSegments(polygon: Point2[], distanceFactor: number) {

        const newPol = [polygon[0]];

        for (let i = 0; i < polygon.length - 1; i++) {
            const current = polygon[i];
            const next = polygon[i + 1];
            const dist = Vector2.Distance(new Vector2(current[0], current[1]), new Vector2(next[0], next[1]));
            if (dist > distanceFactor) {
                newPol.push(next);
            }
        }

        return newPol;
    }

    remove2DLinearSegments(polygon: Point2[], angleFactorDegrees: number) {

        let newPol = [polygon[0]];

        let removedtotal = 0;
        for (let i = 1; i < polygon.length - 1; i++) {
            const previous = polygon[i - 1];
            const current = polygon[i];
            const next = polygon[i + 1];

            const firstVec = new Vector3(previous[0], previous[1], 0).subtract(new Vector3(current[0], current[1], 0)).normalize();
            const secondVec = new Vector3(next[0], next[1], 0).subtract(new Vector3(current[0], current[1], 0)).normalize();

            const angle = Vector3.GetAngleBetweenVectors(firstVec, secondVec, new Vector3(0, 0, 1));
            const angleDeg = Angle.FromRadians(angle).degrees();

            if (Math.abs(angleDeg) >= 180 - (angleFactorDegrees / 2) && Math.abs(angleDeg) <= 180 + (angleFactorDegrees / 2)) {
                removedtotal++;
            } else {
                newPol.push(current);
            }
        }

        newPol.push(polygon[polygon.length - 1]);

        if (removedtotal !== 0) {
            newPol = this.remove2DLinearSegments(newPol, angleFactorDegrees);
        }

        return newPol;
    }

    unionPolylines(polylines: Point2[][], tolerance?: number): Point2[] {
        const polyline = polylines.shift();
        const remainingPolylines = [...polylines];

        const lookForPolylines = (remaining: Point2[][]) => {
            let nextIndex;
            let nextPolyline: Point2[] = remaining.find((p: Point2[], index) => {
                const firstPolylinePoint = p[0];
                const lastPolylinePoint = p[p.length - 1];

                const lastFinal = polyline[polyline.length - 1];
                let result: Point2[];
                if (this.vectorsTheSame(firstPolylinePoint, lastFinal, tolerance)) {
                    nextIndex = index;
                    p.splice(0, 1);
                    result = [...p];
                } else if (this.vectorsTheSame(lastPolylinePoint, lastFinal, tolerance)) {
                    // reverse and append
                    nextIndex = index;
                    const reversed = p.reverse()
                    result = reversed.splice(0, 1);
                }
                return result;
            })
            if (nextPolyline) {
                polyline.push(...nextPolyline);
                if (remaining.length > 0) {
                    remaining.splice(nextIndex, 1);
                    if (remaining && remaining.length > 0) {
                        lookForPolylines(remaining);
                    }
                }
            }
        }

        lookForPolylines(remainingPolylines);

        return polyline;
    }

    vectorsTheSame(first: number[], second: number[], tolerance?: number) {
        let result = true;

        if (first.length === second.length) {

            for (let i = 0; i < first.length; i++) {
                if (!tolerance && first[i] !== second[i]) {
                    result = false;
                    break;
                } else if (tolerance && Math.abs(Math.abs(first[i]) - Math.abs(second[i])) >= tolerance) {
                    result = false;
                    break;
                }
            }
        } else {
            result = false;
        }
        return result;
    }

    pointsCounterClockwise(points: Point2[], log?: boolean) {
        return !this.pointsClockwise(points, log);
    }

    // implemented according to https://stackoverflow.com/questions/1165647/how-to-determine-if-a-list-of-polygon-points-are-in-clockwise-order
    pointsClockwise(points: Point2[], log?: boolean) {
        const assembleResults = [];
        let sum = 0;
        for (let i = 1; i < points.length; i++) {
            const prevPoint = points[i - 1];
            const currentPoint = points[i];

            const res = (currentPoint[0] - prevPoint[0]) * (currentPoint[1] + prevPoint[1]);
            sum += res;
            assembleResults.push(res);
        }

        if (log) {
            console.log('direction was based on points ', points);
            console.log('direction was based on sum ', sum);
        }
        if (sum > 0) {
            return true;
        } else {
            return false;
        }
    }

    getPathsAndHoles(polygonCoordinates: Point2[][][]): Polygon[] {
        const polygonsWithHoles: Polygon[] = [];
        polygonCoordinates.forEach(coord => {
            coord.forEach(u => {
                if (this.pointsCounterClockwise(u)) {
                    // add a new path
                    polygonsWithHoles.push({ path: u, holes: [] });
                } else {
                    // this is a hole for previous path
                    const polygonWithHoles = polygonsWithHoles[polygonsWithHoles.length - 1];
                    if (polygonWithHoles && polygonWithHoles.holes) {
                        polygonWithHoles.holes.push(u.reverse());
                    } else {
                        // this can happen when its hard to determine the direction and wrong call was done on its direction.
                        polygonsWithHoles.push({ path: u.reverse(), holes: [] });
                    }
                }
            });
        })
        return polygonsWithHoles;
    }

    unionPolygons(polygons: Polygon[]): Polygon[] {
        const mappedPolygons = polygons.map(p => [p.path, ...p.holes])

        const p = [...mappedPolygons];
        const adj = mappedPolygons.shift();
        let unionedPolygons: Polygon[] = [];
        try {
            const unioned = pclipping.union(adj as any, ...p as any) as any;
            unioned.forEach(u => {
                const path = u.find(s => this.pointsCounterClockwise(s));
                const holes = u.filter(s => this.pointsClockwise(s));
                holes.forEach(hole => {
                    hole.reverse();
                })
                unionedPolygons.push({
                    path,
                    holes
                })
            })
        } catch (e) {
            console.warn('Something went wrong in union of polygons, thus original polygons will be used', e);
            unionedPolygons = polygons;
        }

        return unionedPolygons;
    }

    flattenPolygons(polygons: Polygon[], dimensions: number): FlatPolygon[] {
        let totalLength = 0
        return polygons.map(polygon => {
            return {
                path: polygon.path.flat(),
                holes: polygon.holes.flat().flat(),
                holeEnds: polygon.holes.map(h => {
                    totalLength += h.length;
                    return totalLength;
                }
                ),
                dimensions
            }
        })
    }

    inflateFlatPolygons(flatPolygons: FlatPolygon[]): Polygon[] {
        return flatPolygons.map(fp => {
            const holeCoordinates: number[][] = [];

            fp.holes.forEach((h, index) => {
                if (index % fp.dimensions === 0) {
                    holeCoordinates.push([h]);
                } else {
                    const hole = holeCoordinates[holeCoordinates.length - 1];
                    hole.push(h);
                }
            });

            const pathCoordinates: number[][] = [];
            fp.path.forEach((p, index) => {
                if (index % fp.dimensions === 0) {
                    pathCoordinates.push([p]);
                } else {
                    const path = pathCoordinates[pathCoordinates.length - 1];
                    path.push(p);
                }
            });

            const holes = fp.holeEnds.map((end, index) => {
                const hole = [];
                let previousEnd = 0;
                if (index > 0) {
                    previousEnd = fp.holeEnds[index - 1];
                }
                for (let i = previousEnd; i < end; i++) {
                    const hc = holeCoordinates[i];
                    hole.push(hc);
                }
                return hole;
            });

            return {
                path: pathCoordinates,
                holes
            }
        });
    }
}